import { useQuery } from '@tanstack/react-query';
import { tagApi } from '@/entities/tag';
import { QUERY_KEYS } from '@/shared/config/constants';
import { cx } from '@/utils/cx';

// `category` recebido pelo step eh apenas semantica do contexto onde a tag
// sera aplicada (BUSCA = "o que quero encontrar" / OFERTA = "o que ofereco").
// O catalogo de tags eh unico no backend; cada tag pode ser usada nos dois
// contextos. Mantemos a prop pra preservar a API publica do componente, mas
// nao filtramos a lista por ela.
type TagCategoryFilter = 'BUSCA' | 'OFERTA';

type TagPickerProps = {
  category: TagCategoryFilter;
  selectedIds: string[];
  onChange: (next: string[]) => void;
  maxSelections: number;
  errorMessage?: string;
};

export const TagPicker = ({
  selectedIds,
  onChange,
  maxSelections,
  errorMessage,
}: TagPickerProps) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEYS.TAGS,
    queryFn: tagApi.list,
    staleTime: 60 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Carregando tags"
        className="flex h-32 items-center justify-center rounded-2xl border border-gray-100 bg-gray-50"
      >
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        Não foi possível carregar as tags. Tente novamente em instantes.
      </p>
    );
  }

  const visibleTags = data.tags;

  const toggle = (id: string) => {
    const exists = selectedIds.includes(id);
    if (exists) {
      onChange(selectedIds.filter((x) => x !== id));
      return;
    }
    if (selectedIds.length >= maxSelections) return;
    onChange([...selectedIds, id]);
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium text-gray-500">
        {selectedIds.length}/{maxSelections} selecionados
      </p>
      <div className="flex flex-wrap gap-2">
        {visibleTags.map((tag) => {
          const isSelected = selectedIds.includes(tag.id);
          const isDisabled = !isSelected && selectedIds.length >= maxSelections;
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => toggle(tag.id)}
              disabled={isDisabled}
              aria-pressed={isSelected}
              className={cx(
                'inline-flex h-10 items-center rounded-full border px-4 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
                isSelected
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300',
                isDisabled && 'cursor-not-allowed opacity-40 hover:border-gray-200',
              )}
            >
              {tag.nome}
            </button>
          );
        })}
      </div>
      {errorMessage ? (
        <p className="text-sm font-medium text-error-primary" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
};
